import { AccountAssetsPanel } from '../service/assets-panel.js';
import { SuccessPlanPanel } from '../revenue/success-panel.js';
import { AccountOnboardingPanel } from '../revenue/onboarding-panel.js';
import { AccountHealthPanel } from '../revenue/health-panel.js';
import { AccountTargetPanel } from '../marketing/target-accounts-panel.js';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { AccountPricelistPanel } from './account-pricelist-panel.js';
import { AccountPartnerPanel } from './account-partner-panel.js';
import { AccountAddressesPanel } from './account-addresses-panel.js';
import { RelationshipMapPanel } from './relationship-map-panel.js';
import { AccountPreferencesPanel } from './preferences-panel.js';
import { CustomerHistoryPanel } from './customer-history-panel.js';
import { AccountTerritoryPanel } from './account-territory-panel.js';
import { api, type ApiError, type GetAccountResult } from '@whinerp/client';
import { EmptyState, ErrorState, MetricTile, Panel, Skeleton } from '@whinerp/ui';
import { formatCount, formatMoney } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { RecordPage, TimelineSkeleton } from './record-page.tsx';

/**
 * SC-08 Company record.
 *
 * The rollups are the reason this screen exists rather than being a contact
 * record with a different title: open pipeline, closed revenue, how many
 * people work here. Every one of them comes from count() or sum() over the
 * deal list definition with sharing applied, so a company header cannot total
 * deals the reader is not allowed to open.
 */

function AccountRecordScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const params = useParams();
  const accountId = params['id'] ?? '';

  const account = useQuery<GetAccountResult, ApiError>({
    queryKey: ['account', accountId],
    queryFn: () => api.getAccount({ path: { accountId } }),
  });

  if (account.isPending) return <AccountRecordLoading />;
  if (account.isError) {
    return <AccountRecordError error={account.error} retry={() => void account.refetch()} />;
  }

  const detail = account.data;
  const trendLabels = {
    up: (change: string) => t('metric.trend.up', { values: { change } }),
    down: (change: string) => t('metric.trend.down', { values: { change } }),
    flat: t('metric.trend.flat'),
  };

  return (
    <RecordPage
      objectKey="account"
      recordId={accountId}
      title={detail.account.name}
      subtitle={detail.account.parentName ?? detail.account.industry ?? undefined}
      headerFieldKeys={['industry', 'website']}
      banner={
        <Panel data-region="rollup">
          <div className="flex flex-col gap-4">
            {/* This company's own figures, always, and labelled as its own the
                moment there is a group they could be confused with. A tile
                that silently changed scope when a subsidiary was added is the
                worst version of this screen. */}
            <div className="flex flex-col gap-1">
              {detail.group ? (
                <span data-scope="own" className="text-micro uppercase tracking-label text-ink-label">
                  {t('account.thisCompany')}
                </span>
              ) : null}
              <div className="flex flex-wrap items-start gap-6">
                <MetricTile
                  align="start"
                  data-metric="own-open"
                  label={t('account.openPipeline')}
                  value={formatMoney(detail.rollup.openValue, format, { currency: detail.rollup.currency })}
                  context={t('home.kpi.dealCount', {
                    values: { count: formatCount(detail.rollup.openDeals, format) },
                  })}
                  labels={trendLabels}
                  href="/deals"
                />
                <MetricTile
                  align="start"
                  data-metric="own-won"
                  label={t('account.wonRevenue')}
                  value={formatMoney(detail.rollup.wonValue, format, { currency: detail.rollup.currency })}
                  labels={trendLabels}
                />
                <MetricTile
                  align="start"
                  data-metric="own-people"
                  label={t('account.people')}
                  value={formatCount(detail.rollup.contacts, format)}
                  labels={trendLabels}
                  href={`/contacts?accountId=${accountId}`}
                />
                <MetricTile
                  align="start"
                  data-metric="own-cases"
                  label={t('account.openCases')}
                  value={formatCount(detail.rollup.openCases, format)}
                  labels={trendLabels}
                />
              </div>
            </div>

            {/* The group, drawn only when there is one. A "group total" over a
                company with no subsidiaries is a number whose scope the reader
                cannot check. */}
            {detail.group ? (
              <div className="flex flex-col gap-1 border-t border-t-rule pt-3" data-region="group-rollup">
                <span data-scope="group" className="text-micro uppercase tracking-label text-ink-label">
                  {t('account.group')}
                </span>
                <div className="flex flex-wrap items-start gap-6">
                  <MetricTile
                    align="start"
                    data-metric="group-open"
                    label={t('account.openPipeline')}
                    value={formatMoney(detail.group.openValue, format, { currency: detail.rollup.currency })}
                    context={t('account.group.context', {
                      values: { count: formatCount(detail.group.companies, format) },
                    })}
                    labels={trendLabels}
                  />
                  <MetricTile
                    align="start"
                    data-metric="group-won"
                    label={t('account.wonRevenue')}
                    value={formatMoney(detail.group.wonValue, format, { currency: detail.rollup.currency })}
                    labels={trendLabels}
                  />
                  <MetricTile
                    align="start"
                    data-metric="group-people"
                    label={t('account.people')}
                    value={formatCount(detail.group.contacts, format)}
                    labels={trendLabels}
                  />
                  <MetricTile
                    align="start"
                    data-metric="group-cases"
                    label={t('account.openCases')}
                    value={formatCount(detail.group.openCases, format)}
                    labels={trendLabels}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </Panel>
      }
      related={
        <>
        <AccountPricelistPanel accountId={accountId} current={detail.account.pricelistId} />
        <AccountPartnerPanel accountId={accountId} />
        <CustomerHistoryPanel accountId={accountId} />
        <AccountPreferencesPanel accountId={accountId} />
        <RelationshipMapPanel accountId={accountId} />
        <AccountAddressesPanel accountId={accountId} />
        <AccountTerritoryPanel accountId={accountId} />
        <AccountTargetPanel accountId={accountId} />
        <AccountHealthPanel accountId={accountId} />
        <AccountOnboardingPanel accountId={accountId} />
        <SuccessPlanPanel accountId={accountId} />
        <AccountAssetsPanel accountId={accountId} />
        {detail.tree.length > 1 ? (
          <Panel data-region="hierarchy">
            <div className="flex flex-col gap-1">
              <span className="text-micro uppercase tracking-label text-ink-label">
                {t('account.hierarchy')}
              </span>
              {/* An indented tree, each row carrying its own figures.
                  Indentation alone would say the shape and not the point: a
                  group structure is read in order to find where the money is,
                  and a name with no number beside it sends somebody clicking
                  through six subsidiaries to find out. */}
              <ul className="flex flex-col">
                {detail.tree.map((node) => (
                  <li
                    key={node.accountId}
                    data-tree-node={node.accountId}
                    data-depth={node.depth}
                    className="flex items-baseline gap-3 border-t border-t-rule py-1 first:border-t-0"
                  >
                    {/* Padding at the start, so the indent follows the writing
                        direction rather than always growing to the right. */}
                    <span className="min-w-0 flex-1 truncate" style={{ paddingInlineStart: `${node.depth}rem` }}>
                      {node.accountId === accountId ? (
                        <span className="text-body font-medium text-ink">{node.name}</span>
                      ) : (
                        <a
                          href={`/accounts/${node.accountId}`}
                          className="text-body text-ink hover:text-bronze-text"
                        >
                          {node.name}
                        </a>
                      )}
                    </span>
                    <span data-node-open className="shrink-0 text-meta text-ink-meta">
                      {formatMoney(node.figures.openValue, format, { currency: detail.rollup.currency })}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="pt-1 text-meta text-ink-meta">{t('account.hierarchy.scope')}</p>
            </div>
          </Panel>
        ) : null}
        </>
      }
    />
  );
}

function AccountRecordEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Buildings"
      title={t('record.notFound')}
      body={t('record.notFound.body')}
      primary={{ label: t('object.account.plural'), href: '/accounts', icon: 'Buildings' }}
    />
  );
}

function AccountRecordLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-5" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-xl" />
      <Skeleton className="h-block-xs w-full" />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-record">
        <TimelineSkeleton />
        <Skeleton className="h-block-xl w-full" />
      </div>
    </div>
  );
}

function AccountRecordError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="This company could not be loaded"
      body={error instanceof Error ? error.message : 'The record did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-08',
  route: '/accounts/:id',
  surface: 'page',
  title: 'Company record',
  features: ['F-028', 'F-029', 'F-019', 'F-046'],
  entitlement: 'F-028',
  reachedBy: [{ kind: 'screen', from: 'SC-07', how: 'the company on a contact' }],
  components: ['RecordHeader', 'Timeline', 'DetailsPanel', 'MetricTile'],
  elements: ['RecordPage'],
  sampleRecord: { objectKey: 'account', param: 'id' },
  states: {
    empty: AccountRecordEmpty,
    loading: AccountRecordLoading,
    error: AccountRecordError,
  },
  component: AccountRecordScreen,
});

export default screen;
